import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  $getSelection,
  $isRangeSelection,
  $insertNodes,
  $createParagraphNode,
} from "lexical";
import { HtmlNode } from "./HtmlNode";

/**
 * Detects whether pasted HTML looks like a rich email signature.
 * HubSpot, WiseStamp, Newoldstamp etc. all use deeply nested tables + icon imgs.
 */
function isSignatureHTML(html: string): boolean {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const tables = doc.querySelectorAll("table");
  const hasIconImgs = doc.querySelector(
    'img[alt="mobilePhone"], img[alt="emailAddress"], img[alt="website"]',
  );
  // 3+ nested tables = signature layout
  return tables.length >= 3 || !!hasIconImgs;
}

export default function PastePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const html = event.clipboardData?.getData("text/html");
        const plain = event.clipboardData?.getData("text/plain");
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) return false;

        if (html && isSignatureHTML(html)) {
          // Signature → store raw HTML in HtmlNode (preserves ALL styles)
          $insertNodes([new HtmlNode(html), $createParagraphNode()]);
          return true;
        }

        if (html) {
          // Regular rich text → let Lexical's default RichTextPlugin handle it
          return false;
        }

        if (plain) {
          selection.insertText(plain);
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor]);

  return null;
}
